import { useState } from "react";
import { fmtMoney, fmtMonthShort } from "../../lib/format";
import MoneyCell from "./MoneyCell";
import RatioCell, { decRatio } from "./RatioCell";
import DragScrollContainer from "../DragScrollContainer";
import { currentMonthKey } from "../../lib/dateWindow";
import { Plus, Trash2, Undo2, ChevronUp, ChevronDown, CornerDownRight } from "lucide-react";
import { isSubset, childIdxs, orderedIdxs, parentIdxs } from "../../lib/subsets";

// Spreadsheet-style scope grid.
// Frozen left: ✕ · Scope stage (select + custom) · Type · Contract · Prob.
// Scrollable: Contract value (with stage share %) · Notes · monthly ratio
// cells · Row total with allocation ratio indicator (green ≈1.0 / amber <1.0
// / red >1.0). Past months are subtly shaded and the current month is marked
// with an "as at" dot.
// Sub-consultants are a pinned special row (isSubConsultant) — their fees
// COUNT in every total (probability-weighted like any scope row).
// Time charge and Disbursements are just regular stage picks — no special
// pinned rows; users add a scope row and choose them from the stage dropdown.
export function ScopeGrid({ months, scopes, stages, contractValues, onChange, onAdd, onAddSub, onAddSubset, onRemove, onMove, isAdmin = false, canEditPast = false, hideSub = false }) {
  const W_REMOVE = 56, W_SCOPE = 190, W_TYPE = 92, W_CONTRACT = 104, W_PROB = 64;
  const FROZEN_W = W_REMOVE + W_SCOPE + W_TYPE + W_CONTRACT + W_PROB;
  const M_COL_MIN = 88;
  const CUR = currentMonthKey();
  // Past months are visually shaded for everyone, but only unlocked for
  // callers with the per-user canEditPastMonths flag (Hamish, Andrew, Jo).
  const isPast = (mo) => mo < CUR;
  const lockPast = (mo) => isPast(mo) && !canEditPast;

  const update = (idx, patch) =>
    onChange(scopes.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  // Changing a parent's stage carries its subsets with it — they inherit the
  // stage string, which is what keeps every group-by-stage rollup correct.
  const setStage = (idx, value) => {
    const parentId = scopes[idx].id;
    onChange(scopes.map((s, i) => {
      if (i === idx) return { ...s, stage: value };
      if (s.parentScopeId === parentId) return { ...s, stage: value };
      return s;
    }));
  };
  const updateRev = (idx, mo, val, ratioStr) =>
    onChange(scopes.map((s, i) => {
      if (i !== idx) return s;
      const revenueRatios = { ...(s.revenueRatios || {}) };
      if (ratioStr) revenueRatios[mo] = ratioStr;
      else delete revenueRatios[mo];
      return { ...s, revenue: { ...(s.revenue || {}), [mo]: val }, revenueRatios };
    }));

  const rowTotal = (s) =>
    Object.values(s.revenue || {}).reduce((a, b) => a + (b || 0), 0);

  const subIdx = scopes.findIndex((s) => s.isSubConsultant);
  const subRow = !hideSub && subIdx >= 0 ? scopes[subIdx] : null;
  const firmIdxs = scopes.map((_, i) => i).filter((i) => !scopes[i].isSubConsultant);
  // Grand-total basis: gross (firm stages only) when sub-consultants live in
  // their own section below, otherwise the legacy sub-inclusive total.
  const totIdxs = hideSub ? firmIdxs : scopes.map((_, i) => i);
  const parents = parentIdxs(scopes);

  const monthlyTotal = (mo) =>
    totIdxs.reduce((sum, i) => sum + (scopes[i].revenue?.[mo] || 0), 0);
  const firmMonthly = (mo) =>
    firmIdxs.reduce((sum, i) => sum + (scopes[i].revenue?.[mo] || 0), 0);
  const grandTotal = totIdxs.reduce((sum, i) => sum + rowTotal(scopes[i]), 0);
  const firmTotal = firmIdxs.reduce((sum, i) => sum + rowTotal(scopes[i]), 0);
  const contractTotal = totIdxs.reduce((sum, i) => sum + (Number(scopes[i].contractValue) || 0), 0);
  const firmContractTotal = firmIdxs.reduce((sum, i) => sum + (Number(scopes[i].contractValue) || 0), 0);

  // Stage share denominator: firm-scope contract values only (subs excluded).
  const shareBase = firmContractTotal;

  // Allocation ratio colour: green if ~1.0, amber if <1.0 (under-allocated),
  // red if >1.0 (over-allocated).
  const ratioColour = (dec) => {
    if (dec == null) return "text-[var(--soft-fg)]";
    if (dec > 1.005) return "text-[#6B2F2A] font-medium";  // red — over
    if (dec < 0.995) return "text-[#8a6d1a] font-medium";  // amber — under
    return "text-[#2C4A38] font-medium";                    // green — on target
  };
  const ratioLabel = (dec) => {
    if (dec == null) return "—";
    if (dec > 1.005) return `${decRatio(dec)} · over`;
    if (dec < 0.995) return `${decRatio(dec)} · under`;
    return "1.0 · on target";
  };

  const stickyHeadCls =
    "sticky z-20 bg-[var(--paper-soft)] eyebrow border-b border-[var(--rule)]";
  const stickyBodyCls = "sticky z-10 bg-white p-0";
  const stickySubCls = "sticky z-10 bg-[#F6F4EE] p-0";

  const renderRow = (sc, idx, isSub) => {
    const sub = isSubset(sc);
    const siblings = sub ? childIdxs(scopes, sc.parentScopeId) : parents;
    const pos = siblings.indexOf(idx);
    const total = rowTotal(sc);
    const cv = Number(sc.contractValue) || 0;
    const allocDec = cv > 0 && total > 0 ? (total / cv) : null;
    const bodyCls = isSub ? stickyBodyCls.replace("bg-white", "bg-[#F6F4EE]") : stickyBodyCls;
    const rowBg = isSub ? "bg-[#F6F4EE]" : "";
    const sharePct = !isSub && cv > 0 && shareBase > 0 ? (cv / shareBase) * 100 : null;
    return (
      <tr key={sc.id || idx} className={`border-b border-[var(--rule)] ${rowBg}`} data-testid={isSub ? "sub-consultants-row" : `scope-row-${idx}`}>
        {/* Remove + row order — frozen left */}
        <td className={`${bodyCls} border-r border-[var(--rule)] text-center`} style={{ left: 0, width: W_REMOVE, minWidth: W_REMOVE }}>
          <div className="flex items-center justify-center">
            {!isSub && (
              <div className="flex flex-col">
                <button
                  onClick={() => onMove?.(idx, siblings[pos - 1])}
                  disabled={pos <= 0}
                  className="px-1 text-[var(--soft-fg)] hover:text-[var(--ink)] disabled:opacity-25"
                  title={sub ? "Move subset up (within its stage)" : "Move stage up (subsets follow)"}
                  data-testid={`scope-${idx}-up`}
                >
                  <ChevronUp size={12} />
                </button>
                <button
                  onClick={() => onMove?.(idx, siblings[pos + 1])}
                  disabled={pos < 0 || pos === siblings.length - 1}
                  className="px-1 text-[var(--soft-fg)] hover:text-[var(--ink)] disabled:opacity-25"
                  title={sub ? "Move subset down (within its stage)" : "Move stage down (subsets follow)"}
                  data-testid={`scope-${idx}-down`}
                >
                  <ChevronDown size={12} />
                </button>
              </div>
            )}
            <button
              onClick={() => onRemove(idx)}
              disabled={!isSub && !sub && parents.length <= 1}
              className="p-1.5 text-[var(--soft-fg)] hover:text-[#6B2F2A] disabled:opacity-30"
              data-testid={isSub ? "sub-remove" : `scope-${idx}-remove`}
              title={isSub ? "Remove sub-consultants row" : sub ? "Remove subset row" : "Remove scope row"}
            >
              <Trash2 size={12} />
            </button>
          </div>
        </td>
        <td className={`${bodyCls} border-r border-[var(--rule)]`} style={{ left: W_REMOVE, width: W_SCOPE, minWidth: W_SCOPE }}>
          {isSub ? (
            <div className="px-3 py-2 text-xs font-display tracked-tight uppercase text-[var(--muted-fg)]">Sub consultants</div>
          ) : sub ? (
            /* SUBSET row — free-text label, indented under its parent. Its
               stage is inherited from the parent, which is what makes every
               group-by-stage chart roll it up with no calculation change. */
            <div className="flex items-center pl-3">
              <span className="text-[var(--rule-strong)] pr-1 select-none" aria-hidden>└</span>
              <input
                type="text"
                value={sc.subsetLabel || ""}
                onChange={(e) => update(idx, { subsetLabel: e.target.value })}
                placeholder="subset name…"
                className="w-full px-1 py-2 bg-transparent text-xs outline-none border-0 hover:bg-[var(--paper-soft)]/50 focus:bg-white placeholder:text-[var(--rule-strong)]"
                title={`Subset of ${sc.stage || "parent stage"} — rolls up into it everywhere`}
                data-testid={`scope-${idx}-subsetlabel`}
              />
            </div>
          ) : (
            <div className="flex items-center">
              <div className="flex-1 min-w-0">
                <StageCell
                  value={sc.stage || ""}
                  stages={stages}
                  onChange={(v) => setStage(idx, v)}
                  testid={`scope-${idx}-stage`}
                />
              </div>
              <button
                onClick={() => onAddSubset?.(idx)}
                className="px-1.5 py-1 text-[var(--soft-fg)] hover:text-[var(--ink)] hover:bg-[var(--paper-soft)]"
                title="Add subset row beneath this stage"
                data-testid={`scope-${idx}-add-subset`}
              >
                <CornerDownRight size={12} />
              </button>
            </div>
          )}
        </td>
        <td className={`${bodyCls} border-r border-[var(--rule)]`} style={{ left: W_REMOVE + W_SCOPE, width: W_TYPE, minWidth: W_TYPE }}>
          {isSub ? (
            <div className="px-2 py-2 text-xs text-[var(--soft-fg)]">pass-through</div>
          ) : (
            <select
              value={sc.feeType || "fixed"}
              onChange={(e) => update(idx, { feeType: e.target.value })}
              className={`w-full px-2 py-2 bg-transparent text-xs outline-none border-0 hover:bg-[var(--paper-soft)]/50 focus:bg-white ${sc.feeType === "time_charge" ? "text-[#4a5a7a]" : ""}`}
              data-testid={`scope-${idx}-feetype`}
              title="Fixed fee, or time charge (billed as hours are worked — monthly figures are estimates)"
            >
              <option value="fixed">Fixed</option>
              <option value="time_charge">Time charge</option>
            </select>
          )}
        </td>
        <td className={`${bodyCls} border-r border-[var(--rule)]`} style={{ left: W_REMOVE + W_SCOPE + W_TYPE, width: W_CONTRACT, minWidth: W_CONTRACT }}>
          {isSub ? (
            <div className="px-2 py-2 text-xs text-[var(--soft-fg)]">—</div>
          ) : (
            <select
              value={sc.contract || "Tentative"}
              onChange={(e) => update(idx, { contract: e.target.value })}
              className="w-full px-2 py-2 bg-transparent text-xs outline-none border-0 hover:bg-[var(--paper-soft)]/50 focus:bg-white"
              data-testid={`scope-${idx}-contract`}
            >
              {contractValues.map((c) => <option key={c} value={c}>{c === "Yes" ? "Confirmed" : c}</option>)}
            </select>
          )}
        </td>
        <td className={`${bodyCls} border-r border-[var(--rule-strong)]`} style={{ left: W_REMOVE + W_SCOPE + W_TYPE + W_CONTRACT, width: W_PROB, minWidth: W_PROB }}>
          <input
            type="number" min={0} max={100} step={5}
            value={Math.round((sc.probability ?? 1) * 100)}
            onChange={(e) => update(idx, { probability: Math.max(0, Math.min(100, Number(e.target.value) || 0)) / 100 })}
            className="w-full px-2 py-2 bg-transparent font-mono-num text-xs text-right outline-none border-0 hover:bg-[var(--paper-soft)]/50 focus:bg-white"
            data-testid={isSub ? "sub-prob" : `scope-${idx}-prob`}
          />
        </td>
        <td className={`p-0 border-r border-[var(--rule)] ${isSub ? "" : "bg-[var(--paper-soft)]/30"}`} style={{ minWidth: 108 }}>
          <div className="relative">
            <MoneyCell
              value={sc.contractValue ?? 0}
              onChange={(v) => update(idx, { contractValue: v })}
              allowFlag={false}
              negativeDefault={isSub}
              testid={isSub ? "sub-contractvalue" : `scope-${idx}-contractvalue`}
            />
            {sharePct != null && (
              <div
                className="px-2 pb-1 text-[9px] font-mono-num text-[var(--soft-fg)] text-right leading-none"
                data-testid={`scope-${idx}-share`}
                title="Share of the firm's contract value across all scopes"
              >
                {sharePct >= 1 ? `${Math.round(sharePct)}% of contract` : `${sharePct.toFixed(1)}% of contract`}
              </div>
            )}
          </div>
        </td>
        <td className="p-0 border-r border-[var(--rule)]" style={{ minWidth: 150 }}>
          <input
            type="text"
            value={sc.note || ""}
            onChange={(e) => update(idx, { note: e.target.value })}
            placeholder={isSub ? "who / discipline…" : "optional…"}
            className="w-full px-2 py-2 bg-transparent text-xs outline-none border-0 hover:bg-[var(--paper-soft)]/50 focus:bg-white placeholder:text-[var(--rule-strong)]"
            data-testid={isSub ? "sub-note" : `scope-${idx}-note`}
          />
        </td>
        {months.map((mo) => {
          const past = isPast(mo);
          return (
            <td key={mo} className={`p-0 border-r border-[var(--rule)]/40 align-middle ${past ? "bg-[var(--paper-soft)]/40" : ""}`} style={{ minWidth: M_COL_MIN }}>
              <RatioCell
                cv={cv}
                value={sc.revenue?.[mo] ?? 0}
                ratio={sc.revenueRatios?.[mo] || null}
                onChange={(v, ratioStr) => updateRev(idx, mo, v, ratioStr)}
                testid={isSub ? `sub-${mo}` : `scope-${idx}-${mo}`}
                pastMonth={past}
                readOnly={lockPast(mo)}
                negativeDefault={isSub}
              />
            </td>
          );
        })}
        <td className="p-0 sticky right-0 z-10 border-l border-[var(--rule-strong)] bg-[var(--paper-soft)]" style={{ minWidth: 150, boxShadow: "-1px 0 0 var(--rule-strong)" }}>
          <div className="px-3 py-2 font-mono-num text-xs text-right" data-testid={isSub ? "sub-total" : `scope-${idx}-total`}>
            {total ? fmtMoney(total) : "—"}
            {cv > 0 && (
              <div
                className={`text-[9px] leading-snug ${ratioColour(allocDec)}`}
                data-testid={isSub ? "sub-allocation" : `scope-${idx}-allocation`}
                title={
                  allocDec == null ? "Nothing allocated yet"
                  : allocDec > 1.005 ? "Over-allocated — monthly ratios sum past the full contract value"
                  : allocDec < 0.995 ? "Under-allocated — monthly ratios don't yet fill the contract value"
                  : "On target — monthly ratios total the contract value exactly"
                }
              >
                {ratioLabel(allocDec)} · {fmtMoney(total)} of {fmtMoney(cv)}
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
      <div className="border border-[var(--rule)]" data-testid="scope-grid">
      <DragScrollContainer className="overflow-x-auto thin-scroll">
        <table className="text-xs border-collapse" style={{ minWidth: "100%" }}>
          <thead>
            <tr>
              <th className={`${stickyHeadCls}`} style={{ left: 0, width: W_REMOVE, minWidth: W_REMOVE }} />
              <th className={`${stickyHeadCls} text-left px-3 py-2`} style={{ left: W_REMOVE, width: W_SCOPE, minWidth: W_SCOPE }}>
                <div className="text-[#4a5a7a]">Forecast</div>
                <div className="normal-case tracking-[0.06em] text-[10px] text-[var(--soft-fg)]">scope / stage</div>
              </th>
              <th className={`${stickyHeadCls} text-left px-2 py-2`} style={{ left: W_REMOVE + W_SCOPE, width: W_TYPE, minWidth: W_TYPE }}>Type</th>
              <th className={`${stickyHeadCls} text-left px-2 py-2`}  style={{ left: W_REMOVE + W_SCOPE + W_TYPE, width: W_CONTRACT, minWidth: W_CONTRACT }}>Contract</th>
              <th className={`${stickyHeadCls} text-right px-2 py-2 border-r border-[var(--rule-strong)]`} style={{ left: W_REMOVE + W_SCOPE + W_TYPE + W_CONTRACT, width: W_PROB, minWidth: W_PROB }}>Prob %</th>
              <th className="px-2 py-2 eyebrow text-right border-b border-[var(--rule)] bg-[var(--paper-soft)]/60" style={{ minWidth: 108 }}>Contract value</th>
              <th className="px-2 py-2 eyebrow text-left border-b border-[var(--rule)]" style={{ minWidth: 150 }}>Notes</th>
              {months.map((mo) => {
                const past = isPast(mo);
                return (
                  <th key={mo} className={`px-2 py-2 eyebrow text-right border-b border-[var(--rule)] ${past ? "bg-[var(--paper-soft)]/70 text-[var(--muted-fg)]" : "text-[var(--soft-fg)]"}`} style={{ minWidth: M_COL_MIN }} title={past ? "Past month" : undefined}>
                    {fmtMonthShort(mo)}
                    {mo === CUR && <span className="ml-1 inline-block w-1 h-1 bg-[var(--ink)] rounded-full align-middle" title="Current month (as at 1st)" />}
                  </th>
                );
              })}
              <th className="px-3 py-2 eyebrow text-right border-b border-[var(--rule)] border-l border-[var(--rule-strong)] bg-[var(--paper-soft)] sticky right-0 z-20" style={{ minWidth: 150, boxShadow: "-1px 0 0 var(--rule-strong)" }}>Total · allocated</th>
            </tr>
          </thead>
          <tbody>
            {orderedIdxs(scopes).map((i) => renderRow(scopes[i], i, false))}
            {subRow && renderRow(subRow, subIdx, true)}

            {/* Footer breakdown: firm fees subtotal (when subs exist) then the
                combined CONTRACT TOTAL — the project value used everywhere. */}
            {subRow && (
              <tr>
                <td colSpan={5} className="sticky left-0 z-10 bg-[var(--paper-soft)]/60 px-3 py-1.5 eyebrow border-r border-[var(--rule-strong)] border-t border-[var(--rule)]" style={{ width: FROZEN_W, minWidth: FROZEN_W }}>
                  Firm fees subtotal
                </td>
                <td className="px-2 py-1.5 font-mono-num text-[11px] text-right border-t border-[var(--rule)] bg-[var(--paper-soft)]/60 text-[var(--muted-fg)]" data-testid="firm-contractvalue-subtotal">
                  {firmContractTotal ? fmtMoney(firmContractTotal) : "—"}
                </td>
                <td className="border-t border-[var(--rule)] bg-[var(--paper-soft)]/60" />
                {months.map((mo) => {
                  const v = firmMonthly(mo);
                  const past = isPast(mo);
                  return (
                    <td key={mo} className={`px-2 py-1.5 font-mono-num text-[11px] text-right border-t border-[var(--rule)] border-r border-[var(--rule)]/40 ${past ? "bg-[var(--paper-soft)]/80" : "bg-[var(--paper-soft)]/60"} text-[var(--muted-fg)]`} data-testid={`firm-subtotal-${mo}`}>
                      {v ? fmtMoney(v) : "—"}
                    </td>
                  );
                })}
                <td className="px-3 py-1.5 font-mono-num text-[11px] text-right border-t border-[var(--rule)] border-l border-[var(--rule-strong)] bg-[var(--paper-soft)] text-[var(--muted-fg)] sticky right-0 z-10" style={{ minWidth: 150, boxShadow: "-1px 0 0 var(--rule-strong)" }} data-testid="firm-fees-subtotal">
                  {fmtMoney(firmTotal)}
                </td>
              </tr>
            )}
            <tr>
              <td colSpan={5} className="sticky left-0 z-10 bg-[var(--paper-soft)] px-3 py-2 eyebrow border-r border-[var(--rule-strong)] border-t border-[var(--rule-strong)]" style={{ width: FROZEN_W, minWidth: FROZEN_W }}>
                Contract total{subRow ? " · incl. sub consultants" : ""}{hideSub ? " · gross" : ""}
              </td>
              <td className="px-2 py-2 font-mono-num text-xs text-right border-t border-[var(--rule-strong)] bg-[var(--paper-soft)]" data-testid="contract-value-total">
                {contractTotal ? fmtMoney(contractTotal) : "—"}
              </td>
              <td className="border-t border-[var(--rule-strong)] bg-[var(--paper-soft)]" />
              {months.map((mo) => {
                const v = monthlyTotal(mo);
                const past = isPast(mo);
                return (
                  <td key={mo} className={`px-2 py-2 font-mono-num text-xs text-right border-t border-[var(--rule-strong)] border-r border-[var(--rule)]/40 ${past ? "bg-[var(--paper-soft)]/90" : "bg-[var(--paper-soft)]"}`} data-testid={`monthly-total-${mo}`}>
                    {v ? fmtMoney(v) : "—"}
                  </td>
                );
              })}
              <td className="px-3 py-2 font-mono-num text-xs text-right border-t border-[var(--rule-strong)] border-l border-[var(--rule-strong)] bg-[var(--ink)] text-white sticky right-0 z-10" style={{ minWidth: 150, boxShadow: "-1px 0 0 var(--rule-strong)" }} data-testid="grand-total">
                {fmtMoney(grandTotal)}
              </td>
            </tr>
          </tbody>
        </table>
      </DragScrollContainer>
      <div className="border-t border-[var(--rule)] p-2 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button
            onClick={onAdd}
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--rule-strong)] text-xs font-display tracked-tight uppercase hover:bg-[var(--paper-soft)]"
            data-testid="add-scope"
          >
            <Plus size={11} /> Add scope row
          </button>
          {!subRow && !hideSub && (
            <button
              onClick={onAddSub}
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--rule)] text-xs font-display tracked-tight uppercase text-[var(--muted-fg)] hover:bg-[var(--paper-soft)]"
              data-testid="add-subconsultants"
            >
              <Plus size={11} /> Add sub consultants
            </button>
          )}
        </div>
        <div className="text-[10px] font-mono-num text-[var(--soft-fg)] pr-2">
          Month cells take a ratio of the contract value (<span className="text-[var(--ink)]">5/10</span>), a decimal (<span className="text-[var(--ink)]">0.5</span>) or a dollar amount.
        </div>
      </div>
    </div>
  );
}

// Stage picker — a real dropdown with the 7 canonical stages plus "Custom…"
// which switches the cell to a free-text input (custom labels roll into
// "Other" on charts). The undo arrow returns to the dropdown.
function StageCell({ value, stages, onChange, testid }) {
  const isCanonical = stages.includes(value);
  const [customMode, setCustomMode] = useState(!!value && !isCanonical);

  if (customMode || (value && !isCanonical)) {
    return (
      <div className="flex items-center">
        <input
          autoFocus={customMode && !value}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="custom stage name…"
          className="w-full px-3 py-2 bg-transparent text-xs outline-none border-0 hover:bg-[var(--paper-soft)]/50 focus:bg-white placeholder:text-[var(--rule-strong)]"
          data-testid={testid}
        />
        <button
          type="button"
          onClick={() => { setCustomMode(false); onChange(stages[0] || ""); }}
          className="p-1.5 text-[var(--soft-fg)] hover:text-[var(--ink)] shrink-0"
          title="Back to standard stages"
          data-testid={`${testid}-back`}
        >
          <Undo2 size={11} />
        </button>
      </div>
    );
  }

  return (
    <select
      value={value || ""}
      onChange={(e) => {
        if (e.target.value === "__custom__") { setCustomMode(true); onChange(""); }
        else onChange(e.target.value);
      }}
      className="w-full px-2 py-2 bg-transparent text-xs outline-none border-0 hover:bg-[var(--paper-soft)]/50 focus:bg-white"
      data-testid={testid}
    >
      {!value && <option value="" disabled>select stage…</option>}
      {stages.map((s) => <option key={s} value={s}>{s}</option>)}
      <option value="__custom__">Custom…</option>
    </select>
  );
}
